/**
 * 轨迹地图工具：坐标解析、道路纠偏、方向标记
 */

const GRASP_MAX_POINTS = 500;
const DEFAULT_WALK_SPEED = 1.4;

export function parseLngLat(pair) {
  if (!pair || pair.length < 2) {
    return null;
  }
  const lng = Number(pair[0]);
  const lat = Number(pair[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }
  return [lng, lat];
}

export function parseTrajectoryPath(path) {
  if (!path || !path.length) {
    return [];
  }
  return path.map(parseLngLat).filter(Boolean);
}

export function parseRegionPaths(paths) {
  if (!paths || !paths.length) {
    return [];
  }
  const first = paths[0];
  if (Array.isArray(first) && Array.isArray(first[0]) && !Array.isArray(first[0][0])) {
    return paths.map((ring) => ring.map(parseLngLat).filter(Boolean));
  }
  if (Array.isArray(first) && (typeof first[0] === 'string' || typeof first[0] === 'number')) {
    return [paths.map(parseLngLat).filter(Boolean)];
  }
  return [];
}

export function buildSegmentDurations(times, defaultMs = 2000) {
  if (!times || times.length < 2) {
    return defaultMs;
  }
  const durations = [];
  for (let i = 1; i < times.length; i++) {
    const prev = Number(times[i - 1]);
    const cur = Number(times[i]);
    if (!Number.isFinite(prev) || !Number.isFinite(cur) || cur <= prev) {
      durations.push(defaultMs);
      continue;
    }
    durations.push(Math.min(10000, Math.max(500, cur - prev)));
  }
  return durations;
}

/** 为纠偏服务均匀抽稀，保留首尾 */
export function sampleForGrasp(path, times, speeds, maxPoints = GRASP_MAX_POINTS) {
  if (!path || path.length <= maxPoints) {
    return { path: path || [], times: times || [], speeds: speeds || [] };
  }
  const outPath = [path[0]];
  const outTimes = times && times.length ? [times[0]] : [];
  const outSpeeds = speeds && speeds.length ? [speeds[0]] : [];
  const inner = maxPoints - 2;
  const step = (path.length - 2) / inner;
  for (let i = 1; i <= inner; i++) {
    const idx = Math.min(path.length - 2, Math.round(i * step));
    outPath.push(path[idx]);
    if (times && times.length > idx) outTimes.push(times[idx]);
    if (speeds && speeds.length > idx) outSpeeds.push(speeds[idx]);
  }
  outPath.push(path[path.length - 1]);
  if (times && times.length) outTimes.push(times[times.length - 1]);
  if (speeds && speeds.length) outSpeeds.push(speeds[speeds.length - 1]);
  return { path: outPath, times: outTimes, speeds: outSpeeds };
}

function normalizeTimesMs(times) {
  if (!times || !times.length) {
    return null;
  }
  const ms = times.map((t) => Number(t)).filter((t) => Number.isFinite(t) && t > 0);
  if (!ms.length) return null;
  if (ms[0] < 1e12) {
    return ms.map((t) => t * 1000);
  }
  return ms;
}

function buildGraspInput(path, times, speeds) {
  const ms = normalizeTimesMs(times);
  const baseSec = ms ? Math.floor(ms[0] / 1000) : Math.floor(Date.now() / 1000);
  return path.map((p, i) => {
    let tm;
    if (i === 0) {
      tm = baseSec;
    } else if (ms && ms[i]) {
      tm = Math.max(1, Math.round((ms[i] - ms[0]) / 1000));
    } else {
      tm = i;
    }
    const sp = speeds && speeds[i] > 0 ? speeds[i] : DEFAULT_WALK_SPEED;
    let ag = 0;
    if (i > 0) {
      ag = Math.round(calcBearing(path[i - 1], p));
    }
    return { x: p[0], y: p[1], sp, ag, tm };
  });
}

function parseGraspResult(result) {
  const points = result && result.data && result.data.points;
  if (!points || !points.length) {
    return [];
  }
  return points
    .map((pt) => {
      const lng = pt.x != null ? pt.x : pt.lng;
      const lat = pt.y != null ? pt.y : pt.lat;
      return parseLngLat([lng, lat]);
    })
    .filter(Boolean);
}

/**
 * 高德驾车轨迹纠偏，将 GPS 点贴合道路
 */
export function correctPathWithGraspRoad(path, times, speeds) {
  return new Promise((resolve) => {
    if (!path || path.length < 2 || typeof AMap === 'undefined') {
      resolve({ path: path || [], corrected: false });
      return;
    }
    const sampled = sampleForGrasp(path, times, speeds);
    const originPath = buildGraspInput(sampled.path, sampled.times, sampled.speeds);
    AMap.plugin('AMap.GraspRoad', () => {
      try {
        const grasp = new AMap.GraspRoad();
        grasp.driving(originPath, (error, result) => {
          if (error) {
            resolve({ path, corrected: false, error });
            return;
          }
          const corrected = parseGraspResult(result);
          if (corrected.length >= 2) {
            resolve({ path: corrected, corrected: true, distance: result.data && result.data.distance });
          } else {
            resolve({ path, corrected: false });
          }
        });
      } catch (e) {
        resolve({ path, corrected: false, error: e });
      }
    });
  });
}

/** 相邻两点方位角（度），正北为 0，顺时针 */
export function calcBearing(from, to) {
  const lng1 = (from[0] * Math.PI) / 180;
  const lat1 = (from[1] * Math.PI) / 180;
  const lng2 = (to[0] * Math.PI) / 180;
  const lat2 = (to[1] * Math.PI) / 180;
  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function distanceMeters(a, b) {
  const rad = Math.PI / 180;
  const lat1 = a[1] * rad;
  const lat2 = b[1] * rad;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 6378137 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** 沿路径按间隔（米）取方向箭头锚点 */
export function pickDirectionAnchorIndices(path, intervalMeters = 80) {
  if (!path || path.length < 2) return [];
  const indices = [];
  let acc = 0;
  for (let i = 1; i < path.length; i++) {
    acc += distanceMeters(path[i - 1], path[i]);
    if (acc >= intervalMeters) {
      indices.push(i);
      acc = 0;
    }
  }
  if (!indices.length && path.length > 1) {
    indices.push(Math.floor(path.length / 2));
  }
  return indices;
}
